/**
 * The `/oauth` page's return-origin decision — rudder-specs
 * `lookout-audiences-initiative/activations/account-management` §4.2 (the "A′" relay) and §7.
 *
 * Every OAuth provider redirects to this webapp's `/oauth` (the redirect_uri registered with the
 * provider is static, `${OAUTH_BASE_REDIRECT_URL}/oauth`). Another first-party origin (Lookout) that
 * started the flow appends a `state` naming itself; this module decides whether `/oauth` may hand the
 * provider's callback query on to it. The rules follow the redirect-URI account-takeover RCA
 * (rudder-mcp-server, 2026-07-25):
 *  - ONE algorithm, exact match against a static list baked at build time
 *    (`REACT_APP_OAUTH_RETURN_ORIGINS`) — no prefix, suffix, regex or hostname-only matching;
 *  - compare CANONICAL origins (`new URL(x).origin`), and navigate to the LIST ENTRY, never to the
 *    string the request supplied;
 *  - fail closed: every decode/validation failure is `null`, which the page turns into today's
 *    same-origin BroadcastChannel behaviour — never a redirect.
 */

function canonicalOrigin(candidate: string): string | null {
  try {
    const url = new URL(candidate);
    // A first party never mints an origin with credentials in it. Refusing userinfo outright is what
    // closes the parser-differential class from the RCA (`https://a%2f@b/` — one parser reads host
    // `b`, another reads `a`), rather than trusting whichever host THIS parser picked.
    if (url.username !== '' || url.password !== '') {
      return null;
    }
    // `javascript:`, `data:`, `blob:` and friends canonicalise to the literal string "null".
    return url.origin === 'null' ? null : url.origin;
  } catch {
    return null;
  }
}

/**
 * Parse the build-time allowlist. An entry survives only if it IS a canonical origin already
 * (`new URL(entry).origin === entry`), so a mistyped entry (a path, a trailing slash, mixed case, a
 * default port spelled out) can never match anything — it is dropped, not normalised.
 */
export function parseReturnOrigins(raw: string | undefined): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((entry) => entry.length > 0 && canonicalOrigin(entry) === entry);
}

function decodeState(state: string): { o: string } | null {
  try {
    const json = atob(state.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { v, o } = parsed as { v?: unknown; o?: unknown };
    if (v !== 1 || typeof o !== 'string') {
      return null;
    }
    return { o };
  } catch {
    return null;
  }
}

/**
 * The allowlist entry the flow should return to, or `null`.
 *
 * `selfOrigin` (this page's own origin) never matches, even when listed: a webapp-started flow has
 * no `state` and takes the BroadcastChannel path; a `state` naming the webapp is nonsense.
 */
export function matchReturnOrigin(
  state: string | null | undefined,
  returnOrigins: readonly string[],
  selfOrigin: string,
): string | null {
  if (!state || returnOrigins.length === 0) {
    return null;
  }
  const decoded = decodeState(state);
  if (!decoded) {
    return null;
  }
  const origin = canonicalOrigin(decoded.o);
  if (origin === null || origin === selfOrigin) {
    return null;
  }
  const entry = returnOrigins.find((candidate) => candidate === origin);
  return entry ?? null;
}
